/**
 * GUPM-329 — deslocamento do marco superficial: ΔLeste, ΔNorte e Recalque.
 *
 * O marco é o caso invertido dos demais instrumentos: **a posição é a medição**. A
 * coordenada UTM absoluta (296.462 m) não diz nada ao RT; o que importa é quanto o
 * marco andou desde a implantação, em milímetros.
 *
 * Domínio puro: sem React, sem Supabase, sem DOM.
 */

/** Posição de um marco — a de implantação (referência) ou a de uma campanha. */
export interface PosicaoMarco {
  /** Coordenada leste, em metros (UTM 23S SIRGAS2000). */
  easting: number | null;
  /** Coordenada norte, em metros. */
  northing: number | null;
  /** Cota, em metros. */
  cota: number | null;
}

/**
 * Deslocamento em MILÍMETROS. `null` em cada grandeza que não pôde ser calculada —
 * nunca `0`, que afirmaria "medimos e não houve movimento".
 */
export interface DeslocamentoMarco {
  /** Positivo = a campanha está a LESTE da referência (ver convenção abaixo). */
  deltaLesteMm: number | null;
  /** Positivo = a campanha está ao NORTE da referência. */
  deltaNorteMm: number | null;
  /** **Negativo = o marco AFUNDOU** (a cota caiu) em relação à implantação. */
  recalqueMm: number | null;
}

/**
 * Resolução da medição topográfica: a planilha guarda 4 casas decimais em metros,
 * ou seja **0,1 mm**. Arredondar aqui — e não na tela — evita que cada consumidor
 * invente a sua casa decimal, e some o ruído de ponto flutuante que a própria
 * planilha carrega (`0.00729999999` onde se quer dizer 7,3 mm).
 */
const CASAS_MM = 1;

const M_PARA_MM = 1000;

function diferencaMm(referencia: number | null, campanha: number | null): number | null {
  if (referencia === null || campanha === null) return null;
  const mm = (campanha - referencia) * M_PARA_MM;
  return Number(mm.toFixed(CASAS_MM));
}

/**
 * Deslocamento de uma campanha em relação à posição de implantação.
 *
 * ## A convenção é `campanha − referência` — a da planilha OPERACIONAL
 *
 * As duas planilhas da Galvani trazem, à direita das abas de marcos, uma tabela
 * derivada com os mesmos números e **sinais opostos**:
 *
 * | Planilha | Convenção |
 * |---|---|
 * | consolidada de 02/09 (base da carga) | `referência − campanha` (462 de 462 células) |
 * | **operacional** (SharePoint, mantida viva pela Galvani) | **`campanha − referência`** |
 *
 * O dono decidiu seguir a operacional (28/09/2026): é a planilha de campo, e o número
 * da tela tem de bater com ela **no sinal**. ΔE/ΔN positivos = leste/norte; recalque
 * **negativo = afundou**. Trocar o sinal sem trocar a leitura que o RT faz dele é a
 * família de erro do GUPM-226 (movimento lido como melhora) — por isso os testes
 * ancoram o sinal nos números reais da planilha, e não numa hipótese.
 *
 * ## Ausência não vira zero
 *
 * Cada grandeza é independente: falta de um lado anula só aquela. Um marco sem
 * referência — o `MS-2` da Mina, que existe em campo e nunca teve posição de
 * implantação na planilha — devolve as três `null`. Zero seria uma afirmação sobre
 * uma medição que não houve (GUPM-228, ADR-0027).
 *
 * @param referencia posição de implantação; `null` quando o marco não tem
 * @param campanha posição medida na campanha
 */
export function deslocamentoDoMarco(
  referencia: PosicaoMarco | null,
  campanha: PosicaoMarco | null,
): DeslocamentoMarco {
  if (referencia === null || campanha === null) {
    return { deltaLesteMm: null, deltaNorteMm: null, recalqueMm: null };
  }
  return {
    deltaLesteMm: diferencaMm(referencia.easting, campanha.easting),
    deltaNorteMm: diferencaMm(referencia.northing, campanha.northing),
    recalqueMm: diferencaMm(referencia.cota, campanha.cota),
  };
}
